import React from 'react';
import { useTranslation } from 'react-i18next';

import { Panel, SectionTitle, Button, Icon, Text, Skeleton, Toggle } from '../../../../components';
import { CASINO_SETTINGS, COLORS, COUNTRY_MAP_INK } from '../../../../theme/tokens';
import type { MapTone } from '../../../customizer/country/WorldMap/WorldMap';
import {
  getCountryFilter,
  NOT_DEPLOYED,
  saveCountryFilter,
  CountryFilterError,
  type CountryFilter,
} from '../../country-filter-api';
import { COUNTRY_CODES, listCountries, MAP_SHAPES, type CountryEntry } from '../../country-codes';
import { adLanguageBase } from '../../../creatives/ad-language';
import { InkDefs, InkStop } from '../../../website/website-hub.styles';
import { SectionSubtitle } from '../shared.styles';
import { SaveRow, SavedHint } from '../CasinoTypeSection/CasinoTypeSection.styles';
import {
  Body,
  ListCol,
  SearchBox,
  ListHead,
  BlockedFilter,
  QuickActions,
  QuickAction,
  QuickActionsDivider,
  List,
  Row,
  RowName,
  ToneDot,
  EmptyRow,
  CountryMap,
  MapFoot,
  Summary,
  Legend,
  LegendItem,
  ErrorText,
  MutedNote,
  FailedRow,
  type MapInkPaints,
} from './CountryFilterSection.styles';

export type CountryFilterSectionProps = {
  /** The active brand's casino-instance id; undefined (no brand yet) hides the card. */
  instanceId: string | undefined;
  /** The page's toast. */
  onToast: (message: string, placeholders?: Record<string, string | number>) => void;
};

/** What the card can show for the brand: its setting, or why it has none to show. */
type Load = 'loading' | 'ready' | 'not_deployed' | 'failed';

/** Why the last save did not land. */
type SaveNotice = 'failed' | 'not_deployed';

const EMPTY: ReadonlySet<string> = new Set<string>();

const SAVED_HINT_MS = 1800;

/**
 * A stored setting as the card edits it: the countries that are blocked (the rows switched off). A `block` filter's
 * list is that set as is, so a stored code the card has no row for survives a save; an `allow` filter (stored before
 * the card became one allow list) blocks every other country; no filter blocks none.
 */
const blockedOf = (stored: CountryFilter): ReadonlySet<string> => {
  if (stored.mode === 'allow') {
    const allowed = new Set(stored.countries);
    return new Set(COUNTRY_CODES.filter((code) => !allowed.has(code)));
  }
  return new Set(stored.countries);
};

const sameSet = (a: ReadonlySet<string>, b: ReadonlySet<string>): boolean =>
  a.size === b.size && [...a].every((code) => b.has(code));

const toneOf = (code: string, blocked: ReadonlySet<string>): MapTone => (blocked.has(code) ? 'blocked' : 'allowed');

/**
 * How long a country's sweep is, in lengths of its own box: 3 runs a full box past either edge, so a country shows
 * only the sweep's smooth middle third.
 */
const SWEEP = 3;
const OVER = (SWEEP - 1) / 2;

/** The two diagonals a country's fill alternates between, in bounding-box units: deep corner top-left, then top-right. */
const DIAGONALS = [
  { x1: -OVER, y1: -OVER, x2: 1 + OVER, y2: 1 + OVER },
  { x1: 1 + OVER, y1: -OVER, x2: -OVER, y2: 1 + OVER },
] as const;

const TONES = ['allowed', 'blocked'] as const;

/**
 * The map's gradients: per tone, both diagonals, each laid across a country's own box (so every country carries its
 * own sweep, like the Operations stat glyphs). Rendered in a hidden svg; the map's paths reference them by id.
 */
const MapInk = ({ ids }: { ids: Record<(typeof TONES)[number], readonly [string, string]> }): React.ReactElement => (
  <InkDefs aria-hidden="true" focusable="false">
    <defs>
      {TONES.map((tone) =>
        DIAGONALS.map((d, i) => (
          <linearGradient key={ids[tone][i]} id={ids[tone][i]} x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2}>
            <InkStop offset="0%" $color={COUNTRY_MAP_INK[tone].from} />
            <InkStop offset="100%" $color={COUNTRY_MAP_INK[tone].to} />
          </linearGradient>
        )),
      )}
    </defs>
  </InkDefs>
);

/** Search key: lower case, accents dropped, so "cote" finds Côte d'Ivoire. */
const fold = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();

/**
 * Country access: which countries players can use the website from. One allow list: every country starts switched on
 * (allowed), and switching one off in the searchable list blocks it; the world map previews the result live (green =
 * can use the website, red = blocked). A deliberate pick-then-Save, like Website Currency. Saved as a `block` filter
 * of the countries switched off, so switching every country back on removes the filter.
 *
 * Stored by owner-hub in the brand's ACL tenant (`country-filter-api.ts`). Gated by the `country-filter` feature
 * flag: no service applies the setting yet.
 */
export const CountryFilterSection = ({ instanceId, onToast }: CountryFilterSectionProps): React.ReactElement | null => {
  const { t, i18n } = useTranslation();
  // Country names come in the app's language (its base: `mx`/`arg` -> es, `bra` -> pt, `cfr` -> fr, `za` -> af), so
  // they match the UI and the catalog never re-translates one that happens to be an English key.
  const nameLang = adLanguageBase(i18n.language);
  const inkBase = `country-ink-${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const inkIds = React.useMemo(
    () => ({
      allowed: [`${inkBase}-allowed-a`, `${inkBase}-allowed-b`] as const,
      blocked: [`${inkBase}-blocked-a`, `${inkBase}-blocked-b`] as const,
    }),
    [inkBase],
  );
  const paints = React.useMemo<MapInkPaints>(
    () => ({
      allowed: [`url(#${inkIds.allowed[0]})`, `url(#${inkIds.allowed[1]})`],
      blocked: [`url(#${inkIds.blocked[0]})`, `url(#${inkIds.blocked[1]})`],
    }),
    [inkIds],
  );

  /** The blocked countries as stored; null until the first read for this brand answers. */
  const [baseline, setBaseline] = React.useState<ReadonlySet<string> | null>(null);
  const [load, setLoad] = React.useState<Load>('loading');
  /** The draft: the countries whose rows are switched off. */
  const [blocked, setBlocked] = React.useState<ReadonlySet<string>>(EMPTY);
  /**
   * While the list shows only blocked countries: the ones blocked when the owner turned the view on. Kept as that
   * snapshot (not the live draft), so a row switched back on stays in place and a mis-click can be undone.
   */
  const [blockedView, setBlockedView] = React.useState<ReadonlySet<string> | null>(null);
  const [query, setQuery] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [justSaved, setJustSaved] = React.useState(false);
  const [notice, setNotice] = React.useState<SaveNotice | null>(null);

  // Every read gets an id from a growing counter and only the newest one lands; a brand switch or the save's own
  // answer disowns whatever is still in flight.
  const readSeq = React.useRef(0);
  const readAbort = React.useRef<AbortController | null>(null);
  // Bumped on every brand change, so a save that answers after a switch is dropped.
  const brandEpoch = React.useRef(0);
  const savedTimer = React.useRef<number | undefined>(undefined);

  const disownReads = React.useCallback(() => {
    readSeq.current += 1;
    readAbort.current?.abort();
    readAbort.current = null;
  }, []);

  /** Lands a stored setting as both the baseline and the draft. */
  const applyStored = React.useCallback((next: CountryFilter) => {
    const stored = blockedOf(next);
    setBaseline(stored);
    setBlocked(stored);
    setLoad('ready');
  }, []);

  const read = React.useCallback(
    (id: string) => {
      readSeq.current += 1;
      const seq = readSeq.current;
      readAbort.current?.abort();
      const controller = new AbortController();
      readAbort.current = controller;
      setLoad('loading');
      getCountryFilter(id, controller.signal)
        .then((next) => {
          if (seq === readSeq.current) applyStored(next);
        })
        .catch((err: unknown) => {
          if (seq !== readSeq.current) return;
          setLoad(err instanceof CountryFilterError && err.code === NOT_DEPLOYED ? 'not_deployed' : 'failed');
        })
        .finally(() => {
          if (seq === readSeq.current) readAbort.current = null;
        });
    },
    [applyStored],
  );

  // Brand switch: drop the previous brand's setting and draft, and read the new brand's.
  React.useEffect(() => {
    brandEpoch.current += 1;
    disownReads();
    window.clearTimeout(savedTimer.current);
    setBaseline(null);
    setBlocked(EMPTY);
    setBlockedView(null);
    setQuery('');
    setSaving(false);
    setJustSaved(false);
    setNotice(null);
    if (instanceId) read(instanceId);
  }, [instanceId, read, disownReads]);

  // Unmount: nothing in flight may land, neither a read nor a save.
  React.useEffect(
    () => () => {
      brandEpoch.current += 1;
      disownReads();
      window.clearTimeout(savedTimer.current);
    },
    [disownReads],
  );

  const retry = React.useCallback(() => {
    if (instanceId) read(instanceId);
  }, [instanceId, read]);

  const onEdit = React.useCallback(() => {
    setJustSaved(false);
    setNotice(null);
  }, []);

  // The draft is frozen while a save is in flight: its answer replaces the draft, so an edit made meanwhile would be
  // dropped while the card says it saved. The controls are disabled too; these guards hold even if a click lands.
  // One handler for a row's switch and for the rest of the row (its name), so a click anywhere on the row flips that
  // country. The switch's click stops here, or the row would flip it straight back.
  const onFlip = React.useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      const code = event.currentTarget.dataset.id;
      if (saving || !code) return;
      setBlocked((prev) => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        return next;
      });
      onEdit();
    },
    [saving, onEdit],
  );

  const onToggleBlockedView = React.useCallback(() => {
    setBlockedView((prev) => (prev ? null : new Set(blocked)));
  }, [blocked]);

  const onAllowAll = React.useCallback(() => {
    if (saving) return;
    setBlocked(EMPTY);
    onEdit();
  }, [saving, onEdit]);

  // Kept on top of what is already blocked, so a stored code the card has no row for stays blocked.
  const onBlockAll = React.useCallback(() => {
    if (saving) return;
    setBlocked((prev) => new Set([...prev, ...COUNTRY_CODES]));
    onEdit();
  }, [saving, onEdit]);

  const allBlocked = COUNTRY_CODES.every((code) => blocked.has(code));

  const dirty = baseline !== null && !sameSet(blocked, baseline);

  const onSave = React.useCallback(() => {
    if (!instanceId || baseline === null || saving || sameSet(blocked, baseline)) return;
    const epoch = brandEpoch.current;
    setSaving(true);
    setNotice(null);
    // An empty list removes the filter (the mode is then ignored).
    saveCountryFilter(instanceId, [...blocked].sort(), 'block')
      .then((next) => {
        if (epoch !== brandEpoch.current) return;
        disownReads();
        applyStored(next);
        setJustSaved(true);
        onToast(t('Country access saved'));
        window.clearTimeout(savedTimer.current);
        savedTimer.current = window.setTimeout(() => setJustSaved(false), SAVED_HINT_MS);
      })
      .catch((err: unknown) => {
        if (epoch !== brandEpoch.current) return;
        // Nothing is written on a failure, so the draft stays for another try.
        setNotice(err instanceof CountryFilterError && err.code === NOT_DEPLOYED ? 'not_deployed' : 'failed');
      })
      .finally(() => {
        if (epoch === brandEpoch.current) setSaving(false);
      });
  }, [instanceId, baseline, saving, blocked, disownReads, applyStored, onToast, t]);

  const countries = React.useMemo(() => listCountries(nameLang), [nameLang]);
  const searchKeys = React.useMemo(
    () => new Map(countries.map((c) => [c.code, fold(`${c.name} ${c.code}`)] as const)),
    [countries],
  );

  // The stored blocked countries lead the list (alphabetical within each group). Ordered off the STORED setting, not
  // the draft, so a row never jumps away from the pointer while the owner flips switches.
  const ordered = React.useMemo<CountryEntry[]>(() => {
    if (!baseline || baseline.size === 0) return countries;
    return [...countries.filter((c) => baseline.has(c.code)), ...countries.filter((c) => !baseline.has(c.code))];
  }, [countries, baseline]);

  const needle = fold(query.trim());
  const visible = ordered.filter(
    (c) => (!blockedView || blockedView.has(c.code)) && (!needle || searchKeys.get(c.code)?.includes(needle)),
  );

  const tones = React.useMemo<Record<string, MapTone>>(
    () => Object.fromEntries(MAP_SHAPES.map(([shapeId, code]) => [shapeId, toneOf(code, blocked)])),
    [blocked],
  );

  if (!instanceId) return null;

  const header = (
    <>
      <SectionTitle title={t('Country access')} icon="globe" iconColor={CASINO_SETTINGS.cardIconSelected} />
      <SectionSubtitle
        text="Every country is allowed by default. Turn a country off to block players from it."
        tag="span"
      />
    </>
  );

  if (load === 'not_deployed') {
    return (
      <Panel>
        {header}
        <MutedNote text="Country access becomes available once your website is deployed." tag="span" />
      </Panel>
    );
  }

  if (load === 'failed' && baseline === null) {
    return (
      <Panel>
        {header}
        <FailedRow>
          <ErrorText text="Couldn't load your country settings." tag="span" />
          <Button label={t('Try again')} variant="outline" icon="refresh" onClick={retry} />
        </FailedRow>
      </Panel>
    );
  }

  if (baseline === null) {
    return (
      <Panel>
        {header}
        <Body>
          <Skeleton block height={420} radius={12} />
          <Skeleton block height={260} radius={16} />
        </Body>
      </Panel>
    );
  }

  return (
    <Panel>
      {header}

      <Body>
        <ListCol>
          <SearchBox>
            <Icon name="search" size={16} color={COLORS.textMuted} />
            <input
              type="search"
              value={query}
              placeholder={t('Search countries')}
              aria-label={t('Search countries')}
              onChange={(event) => setQuery(event.target.value)}
            />
          </SearchBox>
          <ListHead>
            <BlockedFilter
              type="button"
              $active={blockedView !== null}
              aria-pressed={blockedView !== null}
              title={blockedView ? t('Show all countries') : t('Show only blocked countries')}
              disabled={blockedView === null && blocked.size === 0}
              onClick={onToggleBlockedView}
            >
              {blocked.size > 0 ? <ToneDot $blocked /> : null}
              <Text text="{{count}} blocked" placeholders={{ count: blocked.size }} tag="span" />
              {blockedView ? <Icon name="close" size={12} /> : null}
            </BlockedFilter>
            <QuickActions>
              <QuickAction type="button" disabled={saving || blocked.size === 0} onClick={onAllowAll}>
                <Text text="Allow all" tag="span" />
              </QuickAction>
              <QuickActionsDivider orientation="vertical" />
              <QuickAction type="button" disabled={saving || allBlocked} onClick={onBlockAll}>
                <Text text="Block all" tag="span" />
              </QuickAction>
            </QuickActions>
          </ListHead>
          <List aria-label={t('Countries')}>
            {visible.length === 0 ? (
              <li>
                <EmptyRow text="No country matches your search." tag="span" />
              </li>
            ) : (
              visible.map((country) => (
                <Row key={country.code} data-id={country.code} $disabled={saving} onClick={onFlip}>
                  {/* Raw: the name is already in the app's language, and must never hit a catalog key. */}
                  <RowName text={country.name} raw tag="span" />
                  {/* Label added to every catalog by hand: the extractor does not read a prop literal. */}
                  <Toggle
                    on={!blocked.has(country.code)}
                    label="Allow {{name}}"
                    labelValues={{ name: country.name }}
                    id={country.code}
                    disabled={saving}
                    onToggle={onFlip}
                  />
                </Row>
              ))
            )}
          </List>
        </ListCol>

        <CountryMap selected={null} tones={tones} $paints={paints} />
        <MapInk ids={inkIds} />
        <MapFoot>
          {blocked.size === 0 ? (
            <Summary text="No restrictions: players from every country can use your website." tag="span" />
          ) : allBlocked ? (
            <Summary text="Every country is blocked: no player can use your website." tag="span" />
          ) : (
            <Summary
              text="Blocked countries: {{count}}. Players from everywhere else can use your website."
              placeholders={{ count: blocked.size }}
              tag="span"
            />
          )}
          <Legend>
            <LegendItem>
              <ToneDot $blocked={false} />
              <Text text="Allowed" tag="span" />
            </LegendItem>
            <LegendItem>
              <ToneDot $blocked />
              <Text text="Blocked" tag="span" />
            </LegendItem>
          </Legend>
        </MapFoot>
      </Body>

      <SaveRow>
        {notice === 'failed' ? (
          <ErrorText text="Couldn't save your country settings. Please try again." tag="span" />
        ) : notice === 'not_deployed' ? (
          <ErrorText text="Your website isn't deployed yet, so country access can't be saved." tag="span" />
        ) : justSaved ? (
          <SavedHint text="Country access saved" tag="span" />
        ) : null}
        <Button
          label={saving ? t('Saving…') : justSaved ? t('Saved') : t('Save changes')}
          icon={justSaved ? 'check' : undefined}
          variant="primary"
          disabled={!dirty || saving}
          onClick={onSave}
        />
      </SaveRow>
    </Panel>
  );
};

export default CountryFilterSection;
